import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const ImpersonationContext = createContext(null);

// Global getter for impersonation state (for use in services outside React)
let globalImpersonationState = { isImpersonating: false, userId: null };

export function getImpersonationHeaders() {
  // First check the global React state
  if (globalImpersonationState.isImpersonating && globalImpersonationState.userId) {
    console.log('🔄 getImpersonationHeaders: Using global state:', globalImpersonationState.userId);
    return { 'X-Impersonate-User': globalImpersonationState.userId };
  }
  
  // Fallback: check sessionStorage directly (for early initialization before React context mounts)
  try {
    const stored = sessionStorage.getItem('impersonation');
    if (stored) {
      const data = JSON.parse(stored);
      if (data?.id) {
        console.log('🔄 getImpersonationHeaders: Using sessionStorage:', data.id);
        return { 'X-Impersonate-User': data.id };
      }
    }
  } catch (e) {
    // Ignore parse errors
  }
  
  console.log('🔄 getImpersonationHeaders: No impersonation active');
  return {};
}

export function ImpersonationProvider({ children }) {
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  const [isImpersonating, setIsImpersonating] = useState(false);

  // Load impersonation state from sessionStorage on mount
  useEffect(() => {
    const stored = sessionStorage.getItem('impersonation');
    if (stored) {
      try {
        const data = JSON.parse(stored);
        setImpersonatedUser(data);
        setIsImpersonating(true);
        // Update global state
        globalImpersonationState = { isImpersonating: true, userId: data.id };
        // Dispatch event so FirebaseAuthContext reloads the user profile
        // Use a small delay to ensure FirebaseAuthContext has mounted and set up its listener
        setTimeout(() => {
          console.log('🔄 Dispatching impersonation-changed on mount for user:', data.id);
          window.dispatchEvent(new CustomEvent('impersonation-changed', { detail: { isImpersonating: true, userId: data.id } }));
        }, 100);
      } catch (e) {
        sessionStorage.removeItem('impersonation');
      }
    }
  }, []);

  // Keep global state in sync
  useEffect(() => {
    globalImpersonationState = {
      isImpersonating,
      userId: impersonatedUser?.id || null,
    };
  }, [isImpersonating, impersonatedUser]);

  const startImpersonation = useCallback((user) => {
    const impersonationData = {
      id: user.id || user.uid,
      email: user.email,
      displayName: user.displayName || user.firstName
        ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
        : user.email,
      startedAt: new Date().toISOString(),
    };
    setImpersonatedUser(impersonationData);
    setIsImpersonating(true);
    sessionStorage.setItem('impersonation', JSON.stringify(impersonationData));
    // Update global state immediately
    globalImpersonationState = { isImpersonating: true, userId: impersonationData.id };
    // Dispatch event so FirebaseAuthContext can reload the user profile
    window.dispatchEvent(new CustomEvent('impersonation-changed', { detail: { isImpersonating: true, userId: impersonationData.id } }));
  }, []);

  const stopImpersonation = useCallback(() => {
    setImpersonatedUser(null);
    setIsImpersonating(false);
    sessionStorage.removeItem('impersonation');
    // Update global state immediately
    globalImpersonationState = { isImpersonating: false, userId: null };
    // Dispatch event so FirebaseAuthContext can reload the user profile
    window.dispatchEvent(new CustomEvent('impersonation-changed', { detail: { isImpersonating: false, userId: null } }));
    // Reload the page to reset all data to the admin's view
    window.location.reload();
  }, []);

  // Get the impersonated user ID for API calls
  const getImpersonatedUserId = useCallback(() => {
    return impersonatedUser?.id || null;
  }, [impersonatedUser]);

  // Get headers object to add to fetch requests
  const getHeaders = useCallback(() => {
    if (isImpersonating && impersonatedUser?.id) {
      return { 'X-Impersonate-User': impersonatedUser.id };
    }
    return {};
  }, [isImpersonating, impersonatedUser]);

  const value = {
    impersonatedUser,
    isImpersonating,
    startImpersonation,
    stopImpersonation,
    getImpersonatedUserId,
    getHeaders,
  };

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
}

export default ImpersonationContext;
